import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { BRAND } from "../lib/constants";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const Login = () => {
  const { login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res =
      mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) navigate("/account");
    else setError(res.error || "Something went wrong");
  };

  const inputCls =
    "w-full rounded-xl border border-[#E2DAD0] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]";

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-[#E2DAD0] p-8">
        <div className="text-center mb-6">
          <img src={BRAND.logo} alt="logo" className="h-16 w-16 rounded-full object-cover ring-2 ring-[#C5A059] mx-auto bg-white" />
          <h1 className="font-serif font-bold text-2xl text-[#1C2B20] mt-4">
            {mode === "login" ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-sm text-[#5A6B5D] mt-1">
            {mode === "login" ? "Sign in to track your orders" : "Register to shop and track your orders"}
          </p>
        </div>

        <div className="flex bg-[#F5F0E6] rounded-full p-1 mb-6">
          <button
            onClick={() => { setMode("login"); setError(""); }}
            data-testid="login-tab"
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${mode === "login" ? "bg-[#1F4D2E] text-white" : "text-[#5A6B5D]"}`}
          >
            Login
          </button>
          <button
            onClick={() => { setMode("register"); setError(""); }}
            data-testid="register-tab"
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${mode === "register" ? "bg-[#1F4D2E] text-white" : "text-[#5A6B5D]"}`}
          >
            Register
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">Full Name</label>
              <input name="name" value={form.name} onChange={change} required className={inputCls} data-testid="auth-name-input" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">Email</label>
            <input name="email" type="email" value={form.email} onChange={change} required className={inputCls} data-testid="auth-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1C2B20] mb-1.5">Password</label>
            <input name="password" type="password" value={form.password} onChange={change} required className={inputCls} data-testid="auth-password-input" />
          </div>
          {error && <p className="text-sm text-[#C86D51]" data-testid="auth-error">{error}</p>}
          {mode === "login" && (
            <div className="text-right -mt-1">
              <Link to="/forgot-password" className="text-xs font-medium text-[#1F4D2E] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] disabled:opacity-60 text-white font-medium rounded-full py-3 transition-colors"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
        <p className="mt-5 text-center text-xs text-[#8A9A8D]">
          <Link to="/shop" className="text-[#1F4D2E] font-medium hover:underline">Continue shopping as guest</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
